/**
 * Offer Engine (international only, v1)
 *
 * A theme-layer discount engine for the custom Stripe-worker checkout. It reads offer
 * config from the "Offer Engine" section (window.OfferEngineConfig) and the shopper's
 * country (window.OfferEngineContext.country), selects ONE active offer for that country,
 * and computes discounted line prices. The discounted prices are what the cart shows and
 * what the checkout payload sends to the worker → Stripe → the Shopify order (approach A:
 * discounted lines, plus an offer tag for reporting).
 *
 * Supported offer types (v1):
 *   - flat_all      : X% off every eligible line
 *   - tiered_spend  : X% off when subtotal >= threshold (highest qualifying tier wins)
 *   - bogo_next     : buy 1, get X% off the next (cheaper) item — every 2nd unit discounted
 *
 * One offer at a time: offers are evaluated in config order; the first that matches the
 * country + is enabled + is in schedule wins. No stacking.
 *
 * The core computeOffer() is a PURE function (no window/Date access) so it can be unit
 * tested in Node. The browser wrapper feeds it live config/context.
 */
(function (root, factory) {
  var api = factory();
  if (typeof module === 'object' && module.exports) {
    module.exports = api; // Node (unit tests)
  }
  if (root) {
    root.OfferEngine = api; // browser
  }
})(typeof window !== 'undefined' ? window : null, function () {
  'use strict';

  function clampPct(p) {
    p = parseFloat(p);
    if (!isFinite(p) || p <= 0) return 0;
    if (p > 100) return 100;
    return p;
  }
  function round2(n) {
    return Math.round((Number(n) + Number.EPSILON) * 100) / 100;
  }
  // Discounted prices must be WHOLE market-currency units, like every other price on the
  // store (INR->market is rounded up to a whole number). This keeps the cart display, the
  // Stripe line-item amounts (unit_amount = price*100 must be an integer), and the Shopify
  // order all consistent — fractional prices would drift and Stripe rejects non-integer cents.
  function roundUnit(n) {
    var v = Math.round(Number(n));
    return v < 0 ? 0 : v;
  }
  function num(n) {
    var v = parseFloat(n);
    return isFinite(v) ? v : 0;
  }

  function countryMatches(offer, country) {
    var list = offer && offer.countries;
    if (!list || !list.length) return false;
    country = String(country || '').toUpperCase();
    for (var i = 0; i < list.length; i++) {
      var c = String(list[i] || '').toUpperCase();
      if (c === 'ALL' || (c && c === country)) return true;
    }
    return false;
  }

  function inSchedule(offer, today) {
    // today + start/end are 'YYYY-MM-DD' strings; lexicographic compare is date-correct.
    if (!today) return true;
    if (offer.start && today < offer.start) return false;
    if (offer.end && today > offer.end) return false;
    return true;
  }

  function selectOffer(config, context) {
    if (!config || !config.offers || !config.offers.length) return null;
    var country = context && context.country ? String(context.country).toUpperCase() : '';
    if (!country) return null;
    var today = (context && context.today) || '';
    for (var i = 0; i < config.offers.length; i++) {
      var o = config.offers[i];
      if (o && o.enabled && countryMatches(o, country) && inSchedule(o, today)) return o;
    }
    return null;
  }

  function bestTierPercent(tiers, subtotal) {
    if (!tiers || !tiers.length) return 0;
    var pct = 0, bestMin = -1;
    for (var i = 0; i < tiers.length; i++) {
      var min = num(tiers[i].min);
      var p = clampPct(tiers[i].percent);
      if (p > 0 && subtotal >= min && min >= bestMin) {
        // highest qualifying threshold wins; tie-break on higher percent
        if (min > bestMin || p > pct) { bestMin = min; pct = p; }
      }
    }
    return pct;
  }

  function blankResult(items) {
    return {
      applied: false,
      offerId: '',
      offerLabel: '',
      offerType: '',
      totalDiscount: 0,
      items: (items || []).map(function (it) {
        return {
          key: it.key,
          quantity: num(it.quantity),
          unitPrice: round2(it.unitPrice),
          discountedUnitPrice: round2(it.unitPrice),
          lineDiscount: 0
        };
      })
    };
  }

  // Apply a flat percent to a set of eligible line indices. Mutates result[], returns total discount.
  // The discounted unit price is rounded to a whole unit; the line discount is derived from it.
  function applyPercent(items, eligible, result, pct) {
    var total = 0;
    for (var k = 0; k < eligible.length; k++) {
      var i = eligible[k];
      var q = num(items[i].quantity);
      var newUnit = roundUnit(items[i].unitPrice * (1 - pct / 100));
      result[i].discountedUnitPrice = newUnit;
      result[i].lineDiscount = round2((items[i].unitPrice - newUnit) * q);
      total += result[i].lineDiscount;
    }
    return round2(total);
  }

  // Buy 1, get pct% off the next item: expand eligible units, discount every 2nd unit
  // (the cheaper one of each pair). Produces a blended discounted unit price per line so
  // the line total (unit x qty) reflects the mix of full + discounted units.
  function applyBogo(items, eligible, result, pct) {
    // Build unit list: { i (line index), price }
    var units = [];
    for (var k = 0; k < eligible.length; k++) {
      var i = eligible[k];
      var q = Math.max(0, Math.round(num(items[i].quantity)));
      for (var u = 0; u < q; u++) units.push({ i: i, price: items[i].unitPrice });
    }
    // Sort by price DESC so the cheaper unit in each pair is the discounted (2nd) one.
    units.sort(function (a, b) { return b.price - a.price; });
    // Discount every 2nd unit (indices 1,3,5,...)
    var discountedCountByLine = {};
    for (var n = 1; n < units.length; n += 2) {
      var li = units[n].i;
      discountedCountByLine[li] = (discountedCountByLine[li] || 0) + 1;
    }
    var total = 0;
    for (var k2 = 0; k2 < eligible.length; k2++) {
      var idx = eligible[k2];
      var qty = Math.max(0, Math.round(num(items[idx].quantity)));
      var dCount = discountedCountByLine[idx] || 0;
      if (dCount <= 0 || qty <= 0) continue;
      var perUnitDisc = items[idx].unitPrice * (pct / 100);
      var lineTotalAfter = items[idx].unitPrice * qty - perUnitDisc * dCount;
      // Blended per-unit price across full + discounted units, rounded to a whole unit.
      var blendedUnit = roundUnit(lineTotalAfter / qty);
      result[idx].discountedUnitPrice = blendedUnit;
      result[idx].lineDiscount = round2((items[idx].unitPrice - blendedUnit) * qty);
      total += result[idx].lineDiscount;
    }
    return round2(total);
  }

  function applyCap(items, result, totalDiscount, cap) {
    if (!cap || cap <= 0 || totalDiscount <= cap) return totalDiscount;
    var scale = cap / totalDiscount;
    var newTotal = 0;
    for (var i = 0; i < result.length; i++) {
      if (result[i].lineDiscount <= 0) continue;
      var qty = num(result[i].quantity) || 1;
      var scaledLineDisc = result[i].lineDiscount * scale;
      var newUnit = roundUnit(items[i].unitPrice - scaledLineDisc / qty);
      result[i].discountedUnitPrice = newUnit;
      result[i].lineDiscount = round2((items[i].unitPrice - newUnit) * qty);
      newTotal += result[i].lineDiscount;
    }
    return round2(newTotal);
  }

  /**
   * PURE core. config = {offers:[...]}, context = {country, today}, items = [{key, quantity, unitPrice, isMto}].
   * unitPrice is in the market/display currency (already converted + rounded by the theme).
   */
  function computeOffer(config, context, items) {
    var out = blankResult(items);
    if (!items || !items.length) return out;
    var offer = selectOffer(config, context);
    if (!offer) return out;

    var eligible = [];
    for (var i = 0; i < items.length; i++) {
      if (offer.exclude_mto && items[i].isMto) continue;
      if (num(items[i].unitPrice) <= 0 || num(items[i].quantity) <= 0) continue;
      eligible.push(i);
    }
    if (!eligible.length) return out;

    var total = 0;
    if (offer.type === 'flat_all') {
      total = applyPercent(items, eligible, out.items, clampPct(offer.percent));
    } else if (offer.type === 'tiered_spend') {
      var subtotal = 0;
      for (var e = 0; e < eligible.length; e++) {
        subtotal += items[eligible[e]].unitPrice * num(items[eligible[e]].quantity);
      }
      var tierPct = bestTierPercent(offer.tiers, subtotal);
      if (tierPct > 0) total = applyPercent(items, eligible, out.items, tierPct);
    } else if (offer.type === 'bogo_next') {
      total = applyBogo(items, eligible, out.items, clampPct(offer.percent));
    }

    total = applyCap(items, out.items, total, num(offer.cap));

    if (total > 0) {
      out.applied = true;
      out.offer = offer;
      out.offerId = offer.id || '';
      out.offerLabel = offer.label || offer.name || offer.id || offer.type;
      out.offerType = offer.type;
      out.totalDiscount = round2(total);
    }
    return out;
  }

  // ---- Browser wrapper: reads live config/context ----

  // Offer schedules are MARKET-AWARE: "today" is the calendar date in the
  // shopper's market timezone, so an offer ending 31 Aug runs through 31 Aug
  // local midnight for that market (not midnight UTC, which cut US evenings
  // short). IANA zones via Intl handle DST correctly; unknown markets and
  // very old browsers fall back to the UTC date.
  var MARKET_TIMEZONES = {
    US: 'America/New_York',
    CA: 'America/Toronto',
    GB: 'Europe/London',
    EU: 'Europe/Paris',
    AE: 'Asia/Dubai',
    SA: 'Asia/Riyadh',
    SG: 'Asia/Singapore',
    MY: 'Asia/Kuala_Lumpur',
    AU: 'Australia/Sydney',
    IN: 'Asia/Kolkata'
  };

  function todayISO(country) {
    var tz = MARKET_TIMEZONES[String(country || '').toUpperCase()];
    if (tz) {
      try {
        // en-CA formats as YYYY-MM-DD, matching the schedule string compare.
        var d = new Date().toLocaleDateString('en-CA', { timeZone: tz });
        if (/^\d{4}-\d{2}-\d{2}$/.test(d)) return d;
      } catch (e) { /* fall through to UTC */ }
    }
    try { return new Date().toISOString().slice(0, 10); } catch (e) { return ''; }
  }
  function liveContext() {
    var ctx = (typeof window !== 'undefined' && window.OfferEngineContext) || {};
    var country = ctx.country || '';
    return { country: country, today: ctx.today || todayISO(country) };
  }

  // Public browser API: apply the active offer to items and return discounted results.
  function apply(items) {
    var config = (typeof window !== 'undefined' && window.OfferEngineConfig) || { offers: [] };
    return computeOffer(config, liveContext(), items);
  }
  function getActiveOffer() {
    var config = (typeof window !== 'undefined' && window.OfferEngineConfig) || { offers: [] };
    return selectOffer(config, liveContext());
  }

  return {
    computeOffer: computeOffer, // pure — used by unit tests
    selectOffer: selectOffer,
    apply: apply,               // browser convenience
    getActiveOffer: getActiveOffer,
    _round2: round2,
    _todayISO: todayISO,        // exposed for tests
    _marketTimezones: MARKET_TIMEZONES
  };
});
